import {
  PlenumFitStatusType,
  RequiredPlenumDimensionsType,
  plenumKitDimensionMatch,
  requiredPlenumDimensionsForProduct
} from 'utils/concealed-plenum-dimensions';
import { airHandlerTypeCodes } from 'data/constants';
import {
  AccessoryProductType,
  AirHandlerProductType,
  RadialDuctEvaluationResultType,
  RadialDuctMetricsType,
  ValidSystemType
} from 'data/types';
import { DuctSuppliesModeType } from 'utils/duct-supplies';
import { returnBranchSizeIn } from 'utils/return-branch-size';

const AIR_HANDLER_SLOT_COUNT = 10;

export type DuctingRunRequirement = {
  runCount: number;
  maxSizeIn: number | null;
  totalCfm: number | null;
  workingFR?: number;
  mode?: DuctSuppliesModeType;
};

export type DuctingKitFitStatusType =
  | 'confirmed'
  | 'unverified'
  | 'insufficient';

export type NeededDuctingKit = {
  product: AccessoryProductType;
  requirement: DuctingRunRequirement | null;
  fitStatus: DuctingKitFitStatusType;
};

export type NeededZoneDuctingProducts = {
  supplyKit: NeededDuctingKit | null;
  returnKit: NeededDuctingKit | null;
  plenum: AccessoryProductType | null;
  plenumFit: PlenumFitStatusType;
  requiredPlenumDims: RequiredPlenumDimensionsType | null;
  totalPrice: number;
};

export type DeriveNeededZoneDuctingProductsParams = {
  airHandlerProduct: AirHandlerProductType;
  ducting: RadialDuctEvaluationResultType | null;
  ductingKitProducts: AccessoryProductType[];
  mode?: DuctSuppliesModeType;
};

export function isConcealedDuctProduct(
  product?: Pick<AirHandlerProductType, 'type'> | null
): boolean {
  if (!product) return false;
  const code = airHandlerTypeCodes[product.type];
  return code === 'concealed' || code === 'concealed_high_static';
}

export type RadialDuctModeKeyType = 'flexOnly' | 'metalFlex' | 'metalOnly';

export const DUCT_MODE_KEYS: ReadonlyArray<RadialDuctModeKeyType> = [
  'flexOnly',
  'metalFlex',
  'metalOnly'
];

export type RadialDuctModeEvaluationType = {
  pass: boolean;
  reasons: string[];
  metrics: RadialDuctMetricsType | null;
};

export type RadialDuctModeEvaluationsType = Partial<
  Record<RadialDuctModeKeyType, RadialDuctModeEvaluationType>
>;

export function getZoneDuctingModes(
  ducting: RadialDuctEvaluationResultType | null
): RadialDuctModeEvaluationsType {
  if (!ducting) return {};
  const withModes = ducting as RadialDuctEvaluationResultType & {
    modes?: RadialDuctModeEvaluationsType;
  };
  if (
    withModes.modes?.flexOnly ||
    withModes.modes?.metalFlex ||
    withModes.modes?.metalOnly
  ) {
    return withModes.modes;
  }
  return {
    flexOnly: {
      pass: ducting.reasons.length === 0,
      reasons: ducting.reasons,
      metrics: ducting.metrics
    }
  };
}

export function preferredDuctModeKey(
  modes: RadialDuctModeEvaluationsType
): RadialDuctModeKeyType | null {
  if (modes.flexOnly?.pass) return 'flexOnly';
  if (modes.metalFlex?.pass) return 'metalFlex';
  if (modes.metalOnly?.pass) return 'metalOnly';
  if (modes.flexOnly) return 'flexOnly';
  if (modes.metalFlex) return 'metalFlex';
  if (modes.metalOnly) return 'metalOnly';
  return null;
}

export function resolveZoneDuctModeKey(
  modes: RadialDuctModeEvaluationsType,
  storedKey: RadialDuctModeKeyType | null | undefined
): RadialDuctModeKeyType | null {
  if (storedKey && modes[storedKey]?.pass) return storedKey;
  return preferredDuctModeKey(modes);
}

export function passingDuctModeKeys(
  modes: RadialDuctModeEvaluationsType
): RadialDuctModeKeyType[] {
  return DUCT_MODE_KEYS.filter((key) => modes[key]?.pass ?? false);
}

export function findZoneDuctingEvaluation(
  validSystems: ValidSystemType[],
  zoneUniqueId: string,
  airHandlerProductId: number
): RadialDuctEvaluationResultType | null {
  for (const system of validSystems) {
    for (let slot = 1; slot <= AIR_HANDLER_SLOT_COUNT; slot += 1) {
      const uniqueKey = `airHandler${slot}Unique` as keyof ValidSystemType;
      const productKey = `airHandler${slot}ProductId` as keyof ValidSystemType;
      const ductingKey = `zone${slot}Ducting` as keyof ValidSystemType;
      if (
        system[uniqueKey] === zoneUniqueId &&
        system[productKey] === airHandlerProductId
      ) {
        const ducting = system[ductingKey] as
          | RadialDuctEvaluationResultType
          | null
          | undefined;
        if (ducting?.metrics) {
          return ducting;
        }
      }
    }
  }
  return null;
}

function maxDuctSize(values: Array<number | null>): number | null {
  const sizes = values.filter(
    (value): value is number => typeof value === 'number' && value > 0
  );
  return sizes.length > 0 ? Math.max(...sizes) : null;
}

function returnDuctCapacityCfm(sizeIn: number): number {
  return (500 * Math.PI * sizeIn * sizeIn) / 576;
}

function requiredReturnDuctSizeIn(
  openings: number,
  requirement: DuctingRunRequirement
): number | null {
  return returnBranchSizeIn({
    openings,
    totalCfm: requirement.totalCfm ?? 0,
    plannedOpenings: requirement.runCount,
    plannedSizeIn: requirement.maxSizeIn,
    mode: requirement.mode,
    workingFR: requirement.workingFR
  });
}

export function deriveSupplyRequirement(
  metrics: RadialDuctMetricsType
): DuctingRunRequirement {
  const supplies = metrics.rooms.flatMap((room) => room.supplies);
  return {
    runCount: supplies.length,
    maxSizeIn: maxDuctSize(supplies.map((supply) => supply.autoSizeIn)),
    totalCfm: supplies.reduce((total, supply) => total + supply.cfm, 0)
  };
}

export function deriveReturnRequirement(
  metrics: RadialDuctMetricsType,
  mode?: DuctSuppliesModeType
): DuctingRunRequirement {
  const returns = metrics.returnRun?.returns ?? [];
  return {
    runCount: returns.length,
    maxSizeIn: maxDuctSize(returns.map((run) => run.autoSizeIn)),
    totalCfm: metrics.returnRun?.cfm ?? null,
    workingFR: metrics.workingFR,
    mode
  };
}

function kitCapacity(kit: AccessoryProductType): {
  quantity: number;
  sizeIn: number;
} {
  return {
    quantity: kit.additionalInfo?.ductQuantity ?? 0,
    sizeIn: kit.additionalInfo?.ductSize ?? 0
  };
}

function supplyKitFits(
  kit: AccessoryProductType,
  requirement: DuctingRunRequirement
): boolean {
  const { quantity, sizeIn } = kitCapacity(kit);
  return (
    quantity >= requirement.runCount && sizeIn >= (requirement.maxSizeIn ?? 0)
  );
}

function returnKitFits(
  kit: AccessoryProductType,
  requirement: DuctingRunRequirement
): boolean {
  const totalCfm = requirement.totalCfm ?? 0;
  const { quantity, sizeIn } = kitCapacity(kit);
  if (totalCfm <= 0 || quantity <= 0 || sizeIn <= 0) return false;
  const requiredSizeIn = requiredReturnDuctSizeIn(quantity, requirement);
  return requiredSizeIn !== null && requiredSizeIn <= sizeIn;
}

function pickKit(
  kits: AccessoryProductType[],
  requirement: DuctingRunRequirement | null
): NeededDuctingKit | null {
  if (kits.length === 0) return null;
  const byPriceAsc = [...kits].sort((a, b) => a.price - b.price);
  if (requirement?.maxSizeIn == null || requirement.runCount === 0) {
    return { product: byPriceAsc[0], requirement, fitStatus: 'unverified' };
  }
  const qualifying = byPriceAsc.filter((kit) =>
    supplyKitFits(kit, requirement)
  );
  if (qualifying.length > 0) {
    return { product: qualifying[0], requirement, fitStatus: 'confirmed' };
  }
  const byCapacityDesc = [...kits].sort((a, b) => {
    const capacityA = kitCapacity(a);
    const capacityB = kitCapacity(b);
    if (capacityB.sizeIn !== capacityA.sizeIn) {
      return capacityB.sizeIn - capacityA.sizeIn;
    }
    return capacityB.quantity - capacityA.quantity;
  });
  return { product: byCapacityDesc[0], requirement, fitStatus: 'insufficient' };
}

function pickReturnKit(
  kits: AccessoryProductType[],
  requirement: DuctingRunRequirement | null
): NeededDuctingKit | null {
  if (kits.length === 0) return null;
  const byPriceAsc = [...kits].sort((a, b) => a.price - b.price);
  if (requirement?.totalCfm == null || requirement.totalCfm <= 0) {
    return { product: byPriceAsc[0], requirement, fitStatus: 'unverified' };
  }
  const qualifying = byPriceAsc.filter((kit) =>
    returnKitFits(kit, requirement)
  );
  if (qualifying.length > 0) {
    return { product: qualifying[0], requirement, fitStatus: 'confirmed' };
  }
  const byTotalCapacityDesc = [...kits].sort((a, b) => {
    const capacityA = kitCapacity(a);
    const capacityB = kitCapacity(b);
    return (
      capacityB.quantity * returnDuctCapacityCfm(capacityB.sizeIn) -
      capacityA.quantity * returnDuctCapacityCfm(capacityA.sizeIn)
    );
  });
  return {
    product: byTotalCapacityDesc[0],
    requirement,
    fitStatus: 'insufficient'
  };
}

export function formatKitSpec(kit: NeededDuctingKit): string | null {
  const { quantity, sizeIn } = kitCapacity(kit.product);
  if (quantity <= 0 || sizeIn <= 0) return null;
  return `${quantity} × ${sizeIn}" ducts`;
}

export function formatKitRequirement(
  kit: NeededDuctingKit,
  runLabel: string
): string | null {
  if (!kit.requirement || kit.requirement.runCount === 0) return null;
  const { runCount, maxSizeIn } = kit.requirement;
  const sizeSuffix = maxSizeIn ? ` @ ${maxSizeIn}"` : '';
  const plural = runCount === 1 ? '' : 's';
  return `${runCount} ${runLabel}${plural}${sizeSuffix}`;
}

export function formatReturnRequirement(kit: NeededDuctingKit): string | null {
  const totalCfm = kit.requirement?.totalCfm ?? null;
  if (totalCfm === null || totalCfm <= 0) {
    return formatKitRequirement(kit, 'return run');
  }
  return `${Math.round(totalCfm)} CFM return`;
}

export function formatReturnInstallNote(kit: NeededDuctingKit): string | null {
  if (kit.fitStatus !== 'confirmed' || !kit.requirement) return null;
  const { quantity } = kitCapacity(kit.product);
  const { runCount, totalCfm } = kit.requirement;
  if (quantity <= 0 || totalCfm === null || totalCfm <= 0) return null;
  if (quantity === runCount) return null;
  const perOpeningCfm = Math.round(totalCfm / quantity);
  const plural = quantity === 1 ? '' : 's';
  return `splits into ${quantity} × ${perOpeningCfm} CFM opening${plural}`;
}

export function supplyKitCoverageLabel(
  kit: NeededDuctingKit | null
): string | null {
  if (kit?.fitStatus !== 'confirmed') return null;
  return `Covered by ${kit.product.name}`;
}

export function returnKitCoverageLabel(
  kit: NeededDuctingKit | null
): string | null {
  if (kit?.fitStatus !== 'confirmed') return null;
  const installNote = formatReturnInstallNote(kit);
  const base = `Covered by ${kit.product.name}`;
  return installNote ? `${base} — ${installNote}` : base;
}

export function findQuotedCoveringKit(
  kind: 'ducting_kit_supply' | 'ducting_kit_return',
  requirement: DuctingRunRequirement | null,
  quotedAccessories: QuotedAccessoryLiteType[],
  ductingKitProducts: AccessoryProductType[]
): AccessoryProductType | null {
  if (!requirement) return null;
  if (kind === 'ducting_kit_supply') {
    if (requirement.maxSizeIn === null || requirement.runCount === 0) {
      return null;
    }
  } else if (requirement.totalCfm === null || requirement.totalCfm <= 0) {
    return null;
  }

  const ductingKitsById = new Map(
    ductingKitProducts.map((kit) => [kit.productId, kit])
  );
  const quotedKits = quotedAccessories
    .filter((accessory) => accessory.quantity > 0)
    .map((accessory) => ductingKitsById.get(accessory.productId))
    .filter((kit): kit is AccessoryProductType => kit?.type === kind);
  const fits = kind === 'ducting_kit_supply' ? supplyKitFits : returnKitFits;
  return quotedKits.find((kit) => fits(kit, requirement)) ?? null;
}

export function quotedSupplyCoverageLabel(
  requirement: DuctingRunRequirement | null,
  quotedAccessories: QuotedAccessoryLiteType[],
  ductingKitProducts: AccessoryProductType[]
): string | null {
  const covering = findQuotedCoveringKit(
    'ducting_kit_supply',
    requirement,
    quotedAccessories,
    ductingKitProducts
  );
  return covering ? `Covered by ${covering.name}` : null;
}

export function quotedReturnCoverageLabel(
  requirement: DuctingRunRequirement | null,
  quotedAccessories: QuotedAccessoryLiteType[],
  ductingKitProducts: AccessoryProductType[]
): string | null {
  const covering = findQuotedCoveringKit(
    'ducting_kit_return',
    requirement,
    quotedAccessories,
    ductingKitProducts
  );
  if (!covering) return null;
  return returnKitCoverageLabel({
    product: covering,
    requirement,
    fitStatus: 'confirmed'
  });
}

export type QuotedAccessoryLiteType = {
  productId: number;
  type?: string;
  quantity: number;
};

export type DuctingQuoteSlotStatusType = 'quoted' | 'alternative' | 'missing';

export type DuctingQuoteStatusType = {
  supplyStatus: DuctingQuoteSlotStatusType | null;
  returnStatus: DuctingQuoteSlotStatusType | null;
  plenumStatus: DuctingQuoteSlotStatusType | null;
  missingCount: number;
  missingTotal: number;
  allCovered: boolean;
};

type DuctingSlotKind = 'ducting_kit_supply' | 'ducting_kit_return' | 'plenum';

function resolveQuotedDuctingKind(
  accessory: QuotedAccessoryLiteType,
  ductingKitsById: Map<number, AccessoryProductType>
): DuctingSlotKind | null {
  if (
    accessory.type === 'ducting_kit_supply' ||
    accessory.type === 'ducting_kit_return' ||
    accessory.type === 'plenum'
  ) {
    return accessory.type;
  }
  const kit = ductingKitsById.get(accessory.productId);
  if (kit?.type === 'ducting_kit_supply') return 'ducting_kit_supply';
  if (kit?.type === 'ducting_kit_return') return 'ducting_kit_return';
  return null;
}

function slotStatus(
  product: AccessoryProductType | null,
  kind: DuctingSlotKind,
  quotedIds: ReadonlySet<number>,
  quotedKinds: ReadonlySet<DuctingSlotKind>
): DuctingQuoteSlotStatusType | null {
  if (!product) return null;
  if (quotedIds.has(product.productId)) return 'quoted';
  if (quotedKinds.has(kind)) return 'alternative';
  return 'missing';
}

export function getDuctingQuoteStatus(
  needed: NeededZoneDuctingProducts,
  quotedAccessories: QuotedAccessoryLiteType[],
  ductingKitProducts: AccessoryProductType[]
): DuctingQuoteStatusType {
  const active = quotedAccessories.filter(
    (accessory) => accessory.quantity > 0
  );
  const quotedIds = new Set(active.map((accessory) => accessory.productId));
  const ductingKitsById = new Map(
    ductingKitProducts.map((kit) => [kit.productId, kit])
  );
  const quotedKinds = new Set(
    active
      .map((accessory) => resolveQuotedDuctingKind(accessory, ductingKitsById))
      .filter((kind): kind is DuctingSlotKind => kind !== null)
  );

  const supplyProduct =
    needed.supplyKit && needed.supplyKit.fitStatus !== 'insufficient'
      ? needed.supplyKit.product
      : null;
  const returnProduct =
    needed.returnKit && needed.returnKit.fitStatus !== 'insufficient'
      ? needed.returnKit.product
      : null;
  const supplyStatus = slotStatus(
    supplyProduct,
    'ducting_kit_supply',
    quotedIds,
    quotedKinds
  );
  const returnStatus = slotStatus(
    returnProduct,
    'ducting_kit_return',
    quotedIds,
    quotedKinds
  );
  const plenumStatus = slotStatus(
    needed.plenum,
    'plenum',
    quotedIds,
    quotedKinds
  );

  const missing: AccessoryProductType[] = [];
  if (supplyStatus === 'missing' && supplyProduct) missing.push(supplyProduct);
  if (returnStatus === 'missing' && returnProduct) missing.push(returnProduct);
  if (plenumStatus === 'missing' && needed.plenum) missing.push(needed.plenum);

  return {
    supplyStatus,
    returnStatus,
    plenumStatus,
    missingCount: missing.length,
    missingTotal: missing.reduce((total, product) => total + product.price, 0),
    allCovered:
      missing.length === 0 &&
      [supplyStatus, returnStatus, plenumStatus].some(
        (status) => status !== null
      )
  };
}

export function formatDuctingQuoteNotice(
  status: DuctingQuoteStatusType | null,
  includeAccessoriesCta = true
): string | null {
  if (!status || status.missingCount === 0) return null;
  const plural = status.missingCount === 1 ? '' : 's';
  const base = `${status.missingCount} required ducting product${plural} missing from this quote`;
  return includeAccessoriesCta
    ? `${base} — add on the Accessories step.`
    : `${base}.`;
}

export function deriveNeededZoneDuctingProducts({
  airHandlerProduct,
  ducting,
  ductingKitProducts,
  mode
}: DeriveNeededZoneDuctingProductsParams): NeededZoneDuctingProducts | null {
  if (!isConcealedDuctProduct(airHandlerProduct)) return null;

  const ductingKitsById = new Map(
    ductingKitProducts.map((kit) => [kit.productId, kit])
  );
  const linkedKits = airHandlerProduct.accessories
    .filter((accessory) => accessory.type === 'concealed_duct_installation_kit')
    .map((accessory) => ductingKitsById.get(accessory.productId))
    .filter((kit): kit is AccessoryProductType => kit !== undefined);

  const supplyKits = linkedKits.filter(
    (kit) => kit.type === 'ducting_kit_supply'
  );
  const returnKits = linkedKits.filter(
    (kit) => kit.type === 'ducting_kit_return'
  );
  const plenumCandidates = [...airHandlerProduct.accessories]
    .filter((accessory) => accessory.type === 'plenum')
    .sort((a, b) => a.price - b.price);
  const requiredPlenumDims =
    requiredPlenumDimensionsForProduct(airHandlerProduct);
  let plenum: AccessoryProductType | null = null;
  let plenumFit: PlenumFitStatusType = 'insufficient';
  if (requiredPlenumDims) {
    plenum =
      plenumCandidates.find(
        (kit) => plenumKitDimensionMatch(kit, requiredPlenumDims) === true
      ) ?? null;
    plenumFit = plenum ? 'confirmed' : 'insufficient';
  } else if (plenumCandidates.length > 0) {
    plenum = plenumCandidates[0] ?? null;
    plenumFit = 'unverified';
  }

  const metrics = ducting?.metrics ?? null;
  const supplyKit = pickKit(
    supplyKits,
    metrics ? deriveSupplyRequirement(metrics) : null
  );
  const returnKit = pickReturnKit(
    returnKits,
    metrics ? deriveReturnRequirement(metrics, mode) : null
  );

  if (!supplyKit && !returnKit && !plenum && !requiredPlenumDims) return null;

  const priceOf = (kit: NeededDuctingKit | null): number =>
    kit && kit.fitStatus !== 'insufficient' ? kit.product.price : 0;
  const totalPrice =
    priceOf(supplyKit) + priceOf(returnKit) + (plenum?.price ?? 0);

  return {
    supplyKit,
    returnKit,
    plenum,
    plenumFit,
    requiredPlenumDims,
    totalPrice
  };
}
